import { createApi } from "@reduxjs/toolkit/query/react";
import { useMemo } from "react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";
import { isAlphaDifficultyLevelsEnabled } from "@/lib/branding";
import { useRuntimeLocale } from "@/shared/I18nRuntimeProvider";
import { normalizeUiLocale } from "@/lib/i18n/uiLocale";

function getLocaleHeader(): string {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "en";
}

function toApiLocale(input: string | null | undefined): string {
  return (normalizeUiLocale(input) ?? "en").split("-")[0].toLowerCase();
}

// --- типы
export interface EnumerationItem {
  raw: string;
  name: string;
}

export interface EnumerationsResponse {
  status: string;
  status_code: string;
  enumerations: Record<string, EnumerationItem[]>;
}

// один элемент перечисления
export interface EnumItem {
  raw: string | number;
  name: string | null;
}

// все возможные ключи (строгий union)
export type EnumerationKey =
  | "AntiPlagiarismStateType"
  | "AppArchType"
  | "AppPlatformType"
  | "AssessmentType"
  | "AssignmentAccessType"
  | "AssignmentActionType"
  | "AssignmentActivityType"
  | "AssignmentApplicationType"
  | "AssignmentAttemptStateType"
  | "AssignmentAttemptStatusType"
  | "AssignmentEndingAtType"
  | "AssignmentIndicatorType"
  | "AssignmentObserverActionType"
  | "AssignmentServiceType"
  | "AssignmentStartingAtType"
  | "AssignmentStatusType"
  | "AssignmentType"
  | "BonusTargetType"
  | "BrowserType"
  | "ChatMessageActionType"
  | "ChatStatusType"
  | "ChatType"
  | "ClassGroupType"
  | "CodeExecutionResultStateType"
  | "CodeExecutionStateType"
  | "ComponentType"
  | "ConditionLogicType"
  | "ConditionOperatorType"
  | "ConditionType"
  | "CourseAvailabilityType"
  | "CourseEndingAtType"
  | "CourseMaterialStatusType"
  | "CourseMaterialType"
  | "CourseStartingAtType"
  | "CourseStatusType"
  | "CourseStepActionType"
  | "CourseStepConditionType"
  | "DifficultLevelQuestionType"
  | "DiscountTargetType"
  | "DiscountType"
  | "DragAndDropQuestionType"
  | "EDocumentType"
  | "ExternalSessionType"
  | "FormInputType"
  | "GuardType"
  | "InviteCodeType"
  | "KDPRequestStatusType"
  | "LessonActionType"
  | "LessonStatusType"
  | "LongTimeType"
  | "MediaStatusType"
  | "MediaType"
  | "NotificationType"
  | "OrderStatusType"
  | "PaymentProviderHandlerType"
  | "PaymentProviderType"
  | "ProductPaymentType"
  | "ProductShipmentType"
  | "ProductStatusType"
  | "ProductType"
  | "QuestionType"
  | "QuizResultType"
  | "RecordType"
  | "RelayServerType"
  | "ReportReasonType"
  | "SchoolType"
  | "ShortTimeType"
  | "StorageTargetType"
  | "StorageType"
  | "StreamingProtocolType"
  | "StreamingProviderType"
  | "TaskStateType"
  | "ThirdPartyServiceStatusType"
  | "TimeType"
  | "TransactionStatusType"
  | "TransactionType"
  | "UserActivityType"
  | "UserAuthType"
  | "UserBalanceType"
  | "UserDocumentType"
  | "UserStatus"
  | "VariantQuestionType"
  | "VerificationActionType"
  | "WebinarSessionKindType"
  | "WebinarSessionRecordType"
  | "WebinarSessionStateType"
  | "WebinarSessionStatusType"
  | "WebinarSessionType"
  | "WebinarStartingAtType"
  | "WebinarStatusType"
  | "WebinarType"
  | "WeekDayType";

const FRONTEND_ASSIGNMENT_ACTION_TYPE_TRANSLATIONS: Record<string, Record<string, string>> = {
  en: {
    env_lost_focus: "Left the tab",
    env_gained_focus: "Returned to the tab",
    env_change_resolution: "Screen size changed",
  },
  ru: {
    env_lost_focus: "Покинул вкладку",
    env_gained_focus: "Вернулся во вкладку",
    env_change_resolution: "Изменился размер окна",
  },
  fr: {
    env_lost_focus: "A quitté l'onglet",
    env_gained_focus: "Retourné à l'onglet",
    env_change_resolution: "Taille de l'écran modifiée",
  },
  "fr-ca": {
    env_lost_focus: "A quitté l'onglet",
    env_gained_focus: "Retourné à l'onglet",
    env_change_resolution: "Taille de l'écran modifiée",
  },
  es: {
    env_lost_focus: "Salió de la pestaña",
    env_gained_focus: "Volvió a la pestaña",
    env_change_resolution: "El tamaño de la pantalla ha cambiado",
  },
  kk: {
    env_lost_focus: "Өткізілген қойындыдан шықты",
    env_gained_focus: "Қайта қойындыға оралды",
    env_change_resolution: "Экран өлшемі өзгерді",
  },
  zh: {
    env_lost_focus: "离开标签页",
    env_gained_focus: "返回标签页",
    env_change_resolution: "屏幕大小已更改",
  },
};

export const enumerationApi = createApi({
  reducerPath: "enumerationApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["Enumerations"],
  endpoints: (builder) => ({
    // Получаем все enum одним запросом
    getEnumerations: builder.query<
      EnumerationsResponse["enumerations"],
      string | void
    >({
      query: (locale) => ({
        url: `/enums.json`,
        method: "GET",
        headers: locale ? { locale } : undefined,
      }),
      transformResponse: (
        response: EnumerationsResponse,
        _meta,
        locale,
      ) => {
        const localeKey = locale || "en";
        const translations =
          FRONTEND_ASSIGNMENT_ACTION_TYPE_TRANSLATIONS[localeKey] ||
          FRONTEND_ASSIGNMENT_ACTION_TYPE_TRANSLATIONS.en;

        const existingActionTypes =
          response.enumerations.AssignmentActionType ?? [];
        const actionTypeMap = new Map(
          existingActionTypes.map((item) => [item.raw, item]),
        );

        Object.entries(translations).forEach(([raw, name]) => {
          if (!actionTypeMap.has(raw)) {
            actionTypeMap.set(raw, { raw, name });
          }
        });

        response.enumerations.AssignmentActionType =
          Array.from(actionTypeMap.values());

        return response.enumerations;
      },
      providesTags: ["Enumerations"],
      keepUnusedDataFor: 3600, // кэш 1 час
    }),
  }),
});

export const useEnumeration = (key: EnumerationKey, locale?: string) => {
  const { locale: runtimeLocale } = useRuntimeLocale();
  const { data, isLoading, isFetching, error } =
    enumerationApi.useGetEnumerationsQuery(
      toApiLocale(locale || runtimeLocale || getLocaleHeader()),
    );

  const items = useMemo(() => {
    if (!data) return [];
    const list = data[key] || [];
    if (
      key === "DifficultLevelQuestionType" &&
      !isAlphaDifficultyLevelsEnabled()
    ) {
      return list.filter((item) => !item.raw.endsWith("_alpha"));
    }
    return list;
  }, [data, key]);

  return {
    data: items,
    isLoading,
    isFetching,
    error,
  };
};
